import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/assets";

const Shell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen bg-[var(--bg)] grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--ink)] text-white relative overflow-hidden">
      <div className="absolute -right-32 -bottom-32 h-[28rem] w-[28rem] rounded-full bg-[var(--blue)] blur-[120px] opacity-60" />
      <Link to="/"><img src={ASSETS.logo} alt="Lowpages" className="h-8 brightness-0 invert" /></Link>
      <div className="relative">
        <h2 className="font-display font-bold text-4xl xl:text-5xl tracking-tight leading-[1]">Sua IA gera a página.<br /><span className="text-slate-400">O Lowpages coloca no ar.</span></h2>
        <p className="mt-6 text-slate-300 max-w-md">Importe o HTML, edite clicando e publique com um clique. Sem hospedagem, sem DNS, sem código.</p>
      </div>
      <span className="font-mono text-xs text-slate-500">© 2026 Lowpages</span>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-md">
        <Link to="/" className="lg:hidden inline-block mb-8"><img src={ASSETS.logo} alt="Lowpages" className="h-7" /></Link>
        <h1 className="font-display font-bold text-3xl tracking-tight text-[var(--ink)]">{title}</h1>
        {subtitle && <p className="mt-2 text-slate-500">{subtitle}</p>}
        <div className="mt-8">{children}</div>
        {footer && <div className="mt-8 text-sm text-slate-500">{footer}</div>}
      </div>
    </div>
  </div>
);

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <input {...props} className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-[var(--blue)] focus:ring-4 ring-[var(--blue)]/10" />
  </label>
);

const GoogleButton = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" data-testid="google-login-button" onClick={go} className="w-full h-11 rounded-xl border border-slate-200 bg-white font-medium text-sm flex items-center justify-center gap-3 hover:bg-slate-50 transition-colors">
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z" /><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v8.1h12.8c-.3 2.1-1.7 5.3-4.8 7.4l7.4 5.7c4.4-4.1 7.1-10.1 7.1-17.2z" /><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z" /><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2 1.4-4.7 2.4-8.5 2.4-6.3 0-11.7-4.1-13.6-9.9l-7.8 6C6.5 42.6 14.6 48 24 48z" /></svg>
      Continuar com Google
    </button>
  );
};

export const Login = () => {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      setUser(data);
      nav(loc.state?.from || "/app", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Entrar" subtitle="Bem-vindo de volta." footer={<>Ainda não tem conta? <Link to="/register" data-testid="go-register-link" className="text-[var(--blue)] font-medium">Criar conta grátis</Link></>}>
      <GoogleButton />
      <div className="my-6 flex items-center gap-4 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />ou com e-mail<span className="h-px flex-1 bg-slate-200" /></div>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="E-mail" type="email" required data-testid="login-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Senha" type="password" required data-testid="login-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="flex justify-end -mt-2"><Link to="/forgot" data-testid="forgot-link" className="text-xs text-slate-500 hover:text-[var(--blue)]">Esqueci minha senha</Link></div>
        <button data-testid="login-submit-button" disabled={busy} className="btn btn-primary justify-center disabled:opacity-60">{busy ? "Entrando…" : "Entrar"}</button>
      </form>
    </Shell>
  );
};

export const Register = () => {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", f);
      setUser(data);
      nav(loc.state?.from || "/app", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Criar conta" subtitle="Teste grátis. Cancele quando quiser." footer={<>Já tem conta? <Link to="/login" data-testid="go-login-link" className="text-[var(--blue)] font-medium">Entrar</Link></>}>
      <GoogleButton />
      <div className="my-6 flex items-center gap-4 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />ou com e-mail<span className="h-px flex-1 bg-slate-200" /></div>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Nome" required data-testid="register-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label="E-mail" type="email" required data-testid="register-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Senha (mín. 6)" type="password" required minLength={6} data-testid="register-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <button data-testid="register-submit-button" disabled={busy} className="btn btn-primary justify-center disabled:opacity-60">{busy ? "Criando…" : "Criar conta"}</button>
      </form>
    </Shell>
  );
};

export const Forgot = () => {
  const [email, setEmail] = useState("");
  const [link, setLink] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setLink(data.reset_link || "");
      toast.success("Se o e-mail existir, enviamos o link de redefinição.");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Shell title="Esqueci minha senha" subtitle="Informe seu e-mail para redefinir." footer={<Link to="/login" className="text-[var(--blue)] font-medium">Voltar para entrar</Link>}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="E-mail" type="email" required data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="forgot-submit-button" className="btn btn-primary justify-center">Enviar link</button>
      </form>
      {link && <div data-testid="forgot-reset-link" className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800 break-all">Envio de e-mail ainda não configurado. Use este link: <a href={link} className="underline">{link}</a></div>}
    </Shell>
  );
};

export const Reset = () => {
  const { token } = useParams();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      toast.success("Senha redefinida. Faça login.");
      nav("/login");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Shell title="Nova senha">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Nova senha" type="password" required minLength={6} data-testid="reset-password-input" value={pwd} onChange={(e) => setPwd(e.target.value)} />
        <button data-testid="reset-submit-button" className="btn btn-primary justify-center">Redefinir</button>
      </form>
    </Shell>
  );
};
